import { BRIDGE_MESSAGE_SOURCES } from "@bezoned/desktop-bridge/bridge-common.js";
import { isBridgeMessageTargetForFrontend } from "@bezoned/desktop-bridge/bridge-predicates.js";
import type { JsonMessagePayload } from "@bezoned/desktop-bridge/events.js";
import {
  isOfficePositionBridgeMessage,
  OFFICE_POSITION_MESSAGE_TYPES,
} from "@bezoned/desktop-bridge/office-position-messaging.js";
import { isBridgeDebugEnabled } from "@bezoned/desktop-bridge/table-call-debug-log.js";
import { Tree } from "fluid-framework";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../auth/use-auth.js";
import type { FluidSharedTree } from "../fluid/fluid.client.js";
import { useFluid } from "../fluid/hooks/use-fluid.js";
import { useOffice } from "../office/use-office.js";
import { createPositionDeltaSenders } from "./position-sync/create-position-delta-senders.js";
import { resolveCurrentPosition } from "./position-sync/position-resolver.js";
import { getPositionSignature } from "./position-sync/position-signature.js";
import {
  buildPositionSnapshot,
  getOnlineOccupantIds,
} from "./position-sync/position-state-builder.js";
import { resolveCurrentLocationName } from "./position-sync/resolve-current-location-name.js";
import { useDesktopBridgeRoom } from "./use-desktop-bridge-room.js";
import { useDesktopBridge } from "./use-desktop-bridge.js";
import { useDesktopPhotoSync } from "./use-desktop-photo-sync.js";

const PRESENCE_THROTTLE_MS = 500;
const POSITION_RESYNC_INTERVAL_MS = 30_000;
const DEBUG_SCOPE = "office-position-sync";

interface PresenceSnapshot {
  availability?: string | undefined;
  outOfOffice?: boolean | undefined;
}

const logPositionSyncEvent = (
  type: string,
  payload: Record<string, unknown>
): void => {
  if (!isBridgeDebugEnabled(import.meta.env.DEV)) {
    return;
  }

  console.debug(`[${DEBUG_SCOPE}]`, {
    type,
    ...payload,
    timestamp: Date.now(),
  });
};

const parseOccupantIdsFromSignature = (signature: string): Set<string> => {
  if (!signature) {
    return new Set();
  }
  const firstColon = signature.indexOf(":");
  const secondColon = signature.indexOf(":", firstColon + 1);
  if (firstColon === -1 || secondColon === -1) {
    return new Set();
  }
  const occupantPart = signature.slice(secondColon + 1);
  return new Set(occupantPart.split(",").filter(Boolean));
};

const parseLocationKey = (signature: string): string => {
  if (!signature) {
    return "";
  }
  const secondColon = signature.indexOf(":", signature.indexOf(":") + 1);
  return secondColon === -1 ? signature : signature.slice(0, secondColon);
};

const getUserPresence = (
  userId: string,
  sharedTree: FluidSharedTree
): PresenceSnapshot | undefined => {
  const fluidUser = sharedTree.root.users.find((u) => u?.id === userId);
  if (!fluidUser?.presence) {
    return;
  }
  return {
    availability: fluidUser.presence.availability,
    outOfOffice: fluidUser.presence.outOfOffice?.isOutOfOffice ?? false,
  };
};

const hasPresenceChanged = (
  current: PresenceSnapshot | undefined,
  previous: PresenceSnapshot | undefined
): boolean =>
  current?.availability !== previous?.availability ||
  current?.outOfOffice !== previous?.outOfOffice;

export const DesktopBridgePositionSyncListener = () => {
  const { user } = useAuth();
  const { sharedTree } = useFluid();
  const { meetingRooms, preset, tables } = useOffice();
  const {
    status: bridgeConnectionStatus,
    instanceId,
    onJsonMessage,
    sendJson,
  } = useDesktopBridge();

  const bridgeRoom = useDesktopBridgeRoom();
  const previousBridgeStatusRef = useRef<string | null>(null);
  const positionSignatureRef = useRef<string>("");
  const currentLocationNameRef = useRef<string | null>(null);
  const hasCurrentLocationNameRef = useRef(false);
  const presenceThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const pendingPresenceRef = useRef(false);
  const previousPresenceRef = useRef<Map<string, PresenceSnapshot>>(new Map());
  const [currentOccupantIds, setCurrentOccupantIds] = useState<string[]>([]);

  const {
    sendPositionState,
    sendAvatarAdded,
    sendAvatarRemoved,
    sendAvatarUpdated,
  } = useMemo(
    () =>
      createPositionDeltaSenders({
        bridgeRoom,
        instanceId,
        sendJson,
      }),
    [instanceId, bridgeRoom, sendJson]
  );

  const onPhotoUpdated = useCallback(
    ({ userId, pictureUrl }: { userId: string; pictureUrl: string | null }) => {
      sendAvatarUpdated({ pictureUrl, userId });
    },
    [sendAvatarUpdated]
  );

  const { pictureUrlCacheRef } = useDesktopPhotoSync({
    occupantIds: currentOccupantIds,
    onPhotoUpdated,
    selfUserId: user.id,
  });

  const volatileRef = useRef({
    manifestMeetingRooms: preset.manifest.meetingRooms,
    meetingRooms,
    sharedTree,
    tables,
    user,
  });
  volatileRef.current = {
    manifestMeetingRooms: preset.manifest.meetingRooms,
    meetingRooms,
    sharedTree,
    tables,
    user,
  };

  const sendFullPositionState = useCallback(
    (reason: string) => {
      const v = volatileRef.current;
      const position = resolveCurrentPosition({
        selfUserId: v.user.id,
        sharedTree: v.sharedTree,
      });
      const currentLocationName = resolveCurrentLocationName({
        manifestMeetingRooms: v.manifestMeetingRooms,
        meetingRooms: v.meetingRooms,
        position,
        tables: v.tables,
      });
      const onlineOccupantIds =
        position.type === "none"
          ? []
          : getOnlineOccupantIds({
              alwaysIncludeUserIds: [v.user.id],
              occupantIds: position.occupantIds,
              sharedTree: v.sharedTree,
            });

      const avatars =
        position.type === "none"
          ? []
          : buildPositionSnapshot({
              occupantIds: onlineOccupantIds,
              // Include cached picture URLs so location switches do not drop
              // avatars back to initials when a full state is sent.
              pictureUrlByUserId: pictureUrlCacheRef.current,
              selfName: v.user.name,
              selfUserId: v.user.id,
              sharedTree: v.sharedTree,
            });

      logPositionSyncEvent("full-state", {
        avatarUserIds: avatars.map((avatar) => avatar.userId),
        currentLocationName,
        locationId: position.type === "none" ? null : position.locationId,
        locationType: position.type,
        reason,
      });
      sendPositionState({ avatars, currentLocationName });
      setCurrentOccupantIds(onlineOccupantIds);
      currentLocationNameRef.current = currentLocationName;
      hasCurrentLocationNameRef.current = true;

      previousPresenceRef.current.clear();
      for (const avatar of avatars) {
        if (avatar.presence) {
          previousPresenceRef.current.set(avatar.userId, avatar.presence);
        }
      }
    },
    [pictureUrlCacheRef, sendPositionState]
  );

  const sendFullPositionStateRef = useRef(sendFullPositionState);
  sendFullPositionStateRef.current = sendFullPositionState;

  const sendNewOccupantAvatar = useCallback(
    (userId: string) => {
      const v = volatileRef.current;
      const avatars = buildPositionSnapshot({
        occupantIds: [userId],
        pictureUrlByUserId: pictureUrlCacheRef.current,
        selfName: v.user.name,
        selfUserId: v.user.id,
        sharedTree: v.sharedTree,
      });
      const avatar = avatars.at(0);
      if (!avatar) {
        return;
      }
      sendAvatarAdded(avatar);
      if (avatar.presence) {
        previousPresenceRef.current.set(userId, avatar.presence);
      }
    },
    [pictureUrlCacheRef, sendAvatarAdded]
  );

  const applyOccupantDelta = useCallback(
    (previousOccupantIds: Set<string>, nextOccupantIds: Set<string>) => {
      for (const userId of previousOccupantIds) {
        if (!nextOccupantIds.has(userId)) {
          sendAvatarRemoved(userId);
          previousPresenceRef.current.delete(userId);
        }
      }

      for (const userId of nextOccupantIds) {
        if (!previousOccupantIds.has(userId)) {
          sendNewOccupantAvatar(userId);
        }
      }
    },
    [sendAvatarRemoved, sendNewOccupantAvatar]
  );

  const checkPresenceChanges = useCallback(() => {
    const v = volatileRef.current;
    const position = resolveCurrentPosition({
      selfUserId: v.user.id,
      sharedTree: v.sharedTree,
    });
    const onlineOccupantIds =
      position.type === "none"
        ? []
        : getOnlineOccupantIds({
            alwaysIncludeUserIds: [v.user.id],
            occupantIds: position.occupantIds,
            sharedTree: v.sharedTree,
          });

    if (onlineOccupantIds.length === 0) {
      return;
    }

    for (const userId of onlineOccupantIds) {
      const current = getUserPresence(userId, v.sharedTree);
      const previous = previousPresenceRef.current.get(userId);

      if (!hasPresenceChanged(current, previous)) {
        continue;
      }

      if (current) {
        sendAvatarUpdated({ presence: current, userId });
        previousPresenceRef.current.set(userId, current);
      } else {
        previousPresenceRef.current.delete(userId);
      }
    }
  }, [sendAvatarUpdated]);

  const schedulePresenceCheck = useCallback(() => {
    if (presenceThrottleRef.current) {
      pendingPresenceRef.current = true;
      return;
    }

    checkPresenceChanges();
    presenceThrottleRef.current = setTimeout(() => {
      presenceThrottleRef.current = null;
      if (pendingPresenceRef.current) {
        pendingPresenceRef.current = false;
        checkPresenceChanges();
      }
    }, PRESENCE_THROTTLE_MS);
  }, [checkPresenceChanges]);

  const schedulePresenceCheckRef = useRef(schedulePresenceCheck);
  schedulePresenceCheckRef.current = schedulePresenceCheck;

  const applyOccupantDeltaRef = useRef(applyOccupantDelta);
  applyOccupantDeltaRef.current = applyOccupantDelta;

  // Fluid tree subscription — detects position and presence changes.
  useEffect(() => {
    const v = volatileRef.current;
    positionSignatureRef.current = getPositionSignature({
      selfUserId: v.user.id,
      sharedTree: v.sharedTree,
    });
    currentLocationNameRef.current = resolveCurrentLocationName({
      manifestMeetingRooms: v.manifestMeetingRooms,
      meetingRooms: v.meetingRooms,
      position: resolveCurrentPosition({
        selfUserId: v.user.id,
        sharedTree: v.sharedTree,
      }),
      tables: v.tables,
    });
    hasCurrentLocationNameRef.current = true;

    const unsubscribeTree = Tree.on(sharedTree.root, "treeChanged", () => {
      const nextSignature = getPositionSignature({
        selfUserId: user.id,
        sharedTree,
      });

      if (nextSignature === positionSignatureRef.current) {
        schedulePresenceCheckRef.current();
        return;
      }

      const previousSignature = positionSignatureRef.current;
      positionSignatureRef.current = nextSignature;

      const locationChanged =
        parseLocationKey(previousSignature) !== parseLocationKey(nextSignature);

      if (locationChanged) {
        logPositionSyncEvent("location-changed", {
          nextAvatarUserIds: [...parseOccupantIdsFromSignature(nextSignature)],
          nextLocationKey: parseLocationKey(nextSignature),
          previousAvatarUserIds: [
            ...parseOccupantIdsFromSignature(previousSignature),
          ],
          previousLocationKey: parseLocationKey(previousSignature),
        });

        // Send the full snapshot immediately — no debounce — so the desktop
        // always replaces the list atomically on location change. This prevents
        // occupant-delta messages from landing on the stale (previous-location)
        // avatar list before the full state arrives.
        sendFullPositionStateRef.current("location-changed");
        return;
      }

      const prev = parseOccupantIdsFromSignature(previousSignature);
      const next = parseOccupantIdsFromSignature(nextSignature);
      applyOccupantDeltaRef.current(prev, next);
      setCurrentOccupantIds([...next]);
    });

    return () => {
      unsubscribeTree();
      if (presenceThrottleRef.current) {
        clearTimeout(presenceThrottleRef.current);
        presenceThrottleRef.current = null;
      }
    };
  }, [sharedTree, user.id]);

  useEffect(() => {
    const nextLocationName = resolveCurrentLocationName({
      manifestMeetingRooms: preset.manifest.meetingRooms,
      meetingRooms,
      position: resolveCurrentPosition({
        selfUserId: user.id,
        sharedTree,
      }),
      tables,
    });

    if (!hasCurrentLocationNameRef.current) {
      currentLocationNameRef.current = nextLocationName;
      hasCurrentLocationNameRef.current = true;
      return;
    }

    if (currentLocationNameRef.current === nextLocationName) {
      return;
    }

    currentLocationNameRef.current = nextLocationName;

    if (bridgeConnectionStatus === "ready") {
      sendFullPositionStateRef.current("location-name-changed");
    }
  }, [
    bridgeConnectionStatus,
    meetingRooms,
    preset.manifest.meetingRooms,
    sharedTree,
    tables,
    user.id,
  ]);

  // Bridge ready → push full position state to desktop.
  useEffect(() => {
    if (bridgeConnectionStatus !== "ready") {
      previousBridgeStatusRef.current = bridgeConnectionStatus;
      return;
    }

    if (previousBridgeStatusRef.current !== "ready") {
      sendFullPositionState("bridge-ready");
    }

    previousBridgeStatusRef.current = bridgeConnectionStatus;
  }, [bridgeConnectionStatus, sendFullPositionState]);

  // Periodic resync — guards against dropped delta messages.
  useEffect(() => {
    if (bridgeConnectionStatus !== "ready") {
      return;
    }

    const intervalId = setInterval(() => {
      sendFullPositionStateRef.current("periodic-resync");
    }, POSITION_RESYNC_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [bridgeConnectionStatus]);

  const onBridgeJsonMessage = useCallback(
    (payload: JsonMessagePayload) => {
      if (payload.room !== bridgeRoom) {
        return;
      }

      if (!isOfficePositionBridgeMessage(payload.data)) {
        return;
      }

      if (payload.data.source !== BRIDGE_MESSAGE_SOURCES.DESKTOP) {
        return;
      }

      if (!isBridgeMessageTargetForFrontend(payload.data.target)) {
        return;
      }

      if (payload.data.type !== OFFICE_POSITION_MESSAGE_TYPES.STATE_REQUEST) {
        return;
      }

      const reason = payload.data.payload.reason.trim();
      sendFullPositionStateRef.current(
        reason.length > 0 ? `state-request:${reason}` : "state-request"
      );
    },
    [bridgeRoom]
  );

  useEffect(
    () => onJsonMessage(onBridgeJsonMessage),
    [onBridgeJsonMessage, onJsonMessage]
  );

  return null;
};
